import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Button } from "risa-oasis-ui_v2";
import CustomTable from "../../../../../../components/custom-table/custom-table";
import AddFormMatrix from "../../../../../../components/modals/AddFormMatrix/addFormMatrix";
import DeleteFormMatrixConfirm from "../../../../../../components/modals/DeleteFormMatrixConfirm/deleteFormMatrixConfirm";
import { FormMatrixTableDataModel } from "../../../../../../data-model/tablesData";
import { useModalOpener } from "../../../../../../hooks/useModalOpener";
import { AppDispatch } from "../../../../../../redux/store/store";
import { FormMatrixTableHeader } from "./formMatrixTableData";
import { useFormMatrixData } from "./hooks/useFormMatrixData";
import {
  closeAddFormModal,
  getAddFormButtonMeta,
  handleAddFormCancel,
  handleAddFormClick,
  handleAddFormMatrixSave,
  handleDiscard,
  handleSave,
  handleToggleChange,
} from "./utils/formMatrixHandlers";

const FormMatrix = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [deleteFormName, setDeleteFormName] = useState<string | null>(null);
  const [addFormKey, setAddFormKey] = useState(0);
  const [workingTableData, setWorkingTableData] = useState<
    FormMatrixTableDataModel[]
  >([]);
  const {
    tableData,
    loading,
    error,
    saveNewFormMatrixItem,
    saveFormMatrixData,
    deleteFormMatrixItem,
    mapTableRowToConfigItem,
  } = useFormMatrixData();

  useModalOpener();

  useEffect(() => {
    if (!isDirty) {
      setWorkingTableData(tableData);
    }
  }, [tableData, isDirty]);

  const onToggleChange = (data: {
    name: string;
    required: boolean;
    value: boolean;
  }) => {
    handleToggleChange(data, setWorkingTableData, setIsDirty);
  };

  const onSave = () =>
    handleSave(
      workingTableData,
      mapTableRowToConfigItem,
      saveFormMatrixData,
      setIsDirty,
      setSaving,
    );

  const onDiscard = () =>
    handleDiscard(tableData, setWorkingTableData, setIsDirty);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="form-matrix-layout flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="form-matrix-layout__header flex flex-shrink-0 flex-col border-b border-primaryGray-15 py-4">
          <div className="font-bold">Form Configuration Matrix</div>
          <div className="text-small font-normal">
            Manage form configurations and validation flags.
          </div>
        </div>
        <div className="form-matrix-layout__content mb-1 mt-6 flex min-h-0 flex-1 flex-col overflow-y-auto">
          {error && (
            <div className="h-full w-full text-small text-red-600">
              Failed to load form matrix. Please try again.
            </div>
          )}
          <CustomTable
            tableHeaders={FormMatrixTableHeader}
            tableData={workingTableData}
            tableName="form-matrix"
            itemsPerPage={10}
            pagesPerView={4}
            count={workingTableData.length}
            showOtherButton={true}
            otherButtonMeta={getAddFormButtonMeta(() => {
              handleAddFormClick(dispatch);
            })}
            isFetching={loading}
            onChangeEmit={onToggleChange}
            onDeleteIconClick={setDeleteFormName}
          />
        </div>
        <div className="form-matrix-layout__footer flex flex-shrink-0 flex-row justify-end gap-2 border-t border-primaryGray-15 py-4">
          <Button
            buttonType="secondary"
            size="medium"
            onClick={onDiscard}
            disabled={!isDirty}
            children="Discard"
          />
          <Button
            buttonType="primary"
            size="medium"
            onClick={onSave}
            disabled={!isDirty || saving}
            children="Save"
          />
        </div>
      </div>
      <AddFormMatrix
        key={addFormKey}
        isSaving={saving}
        onCancel={() => handleAddFormCancel(setSaving, setAddFormKey, dispatch)}
        onSave={(formData) =>
          handleAddFormMatrixSave(
            formData,
            saveNewFormMatrixItem,
            setSaving,
            () => closeAddFormModal(setAddFormKey, dispatch),
          )
        }
      />
      <DeleteFormMatrixConfirm
        formName={deleteFormName}
        onDelete={deleteFormMatrixItem}
        onClose={() => setDeleteFormName(null)}
      />
    </div>
  );
};

export default FormMatrix;
